// File: `ArgosFrontend/src/containers/pages/realWorldObjects/CommonFields.tsx`
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton } from '@mui/material';
import ArgosFlexbox from "../../components/Containers/ArgosFlexbox";
import Tooltip from "@mui/material/Tooltip";
import { ArgosEditIcon } from "../../components/Icons/ArgosActionIcons";
import { LegacyRef } from 'react';
import ArgosTextField from '../../components/Form/TextField/ArgosTextField';
import ArgosButton from '../../components/Buttons/ArgosButton';
import {validateURL} from "../../../helpers/url/urlHelpers";

/**
 * Renders the Object ID display (read-only).
 */
export const renderObjectId = ({ objectId, inputStyle }: { objectId: string | null, inputStyle: React.CSSProperties }) => {
  return (
    <div style={{ flex: 1, maxWidth: '325px', marginLeft: 'auto', alignSelf: 'flex-start' }}>
      <label htmlFor="objectId" style={{ display: 'block', marginBottom: '5px', color: '#0173CE' }}>
        Object ID
      </label>
      <input
        id="objectId"
        type="text"
        disabled={true}
        value={objectId ?? ""}
        style={inputStyle}
        readOnly
      />
    </div>
  );
};

/**
 * Renders a button + dialog to update the ID.
 *
 * - `id` is the current id shown in the dialog default.
 * - `handleUpdate` will be called with the new id string when Save is clicked.
 * - `dialogOpen`, `setDialogOpen` control dialog visibility.
 * - `idRef` is a ref to the dialog input (must be a dedicated ref provided by the caller).
 * - `error`, `setError` manage validation state (kept for other validation cases).
 *
 * Note: empty ID is allowed (caller decides how to handle url clearing).
 */
export const renderUpdateID = (
  id: string,
  handleUpdate: (newId: string) => void,
  dialogOpen: boolean,
  setDialogOpen: (open: boolean) => void,
  idRef: LegacyRef<HTMLInputElement> | undefined,
  error: boolean,
  setError: (hasError: boolean) => void
) => {
  return (
    <ArgosFlexbox style={{ marginTop: 20 }}>
      <Tooltip title='Update ID' placement='top' arrow>
        <IconButton
          style={{ width: 60, height: 60 }}
          onClick={() => {
            setError(false);
            setDialogOpen(true);
          }}
        >
          <ArgosEditIcon style={{ width: 30, height: 30 }} />
        </IconButton>
      </Tooltip>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Update ID</DialogTitle>
        <DialogContent>

          {/* key={id} forces remount when id changes so defaultValue is kept in sync */}
          <ArgosTextField
            key={id ?? 'dialog-id'}
            style={{ marginTop: 20 }}
            fullWidth
            focused={true}
            margin="dense"
            onChange={() => {
              // allow empty ID; clear any prior error state
              setError(false);
            }}
            onBlur={() => {
              setError(false);
            }}
            error={error}
            helperText={error ? `Invalid ID` : undefined}
            inputRef={idRef as React.Ref<any>}
            defaultValue={id ?? ""}
            type="text"
            label="ID"
          />
        </DialogContent>

        <DialogActions>
          <ArgosButton onClick={() => setDialogOpen(false)} color="secondary">Cancel</ArgosButton>
          <ArgosButton
            onClick={() => {
              const newVal = (idRef && (idRef as any).current) ? ((idRef as any).current.value ?? "").toString().trim() : "";
              // allow empty ID; caller will decide how to handle URL
              setError(false);
              handleUpdate(newVal);
              setDialogOpen(false);
            }}
            color="primary"
          >
            Save
          </ArgosButton>
        </DialogActions>
      </Dialog>
    </ArgosFlexbox>
  );
};


/**
 * @description This function renders a button that opens a dialog for updating the URL.
 * This method is kept unchanged per request.
 */
export const renderUpdateURL = (url: string, handleUpdate: () => void, dialogOpen: boolean, setDialogOpen: (open: boolean) => void, urlRef: LegacyRef<HTMLInputElement> | undefined, error: boolean, setError: (hasError: boolean) => void) => {

  return (
    <ArgosFlexbox style={{ marginTop: 20 }}>
      <Tooltip title='Update URL' placement='top' arrow>
        <IconButton
          style={{ width: 60, height: 60 }}
          onClick={() => setDialogOpen(true)}
        >
          <ArgosEditIcon style={{ width: 30, height: 30 }} />
        </IconButton>
      </Tooltip>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle>Update URL</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Enter updated URL.
            </DialogContentText>
            <ArgosTextField
              style={{marginTop: 20}}
              fullWidth
              focused={true}
              margin="dense"
              onBlur={(e: any) => setError(e.target.value.trim() ? !validateURL(e.target.value) : false)}
              error={error}
              helperText={error && `Invalid URL`}

              inputRef={urlRef as React.Ref<any>}
              defaultValue={url ?? ""}

              type="text"
              label="URL"
            />
          </DialogContent>
          {!error && <DialogActions>
            <ArgosButton onClick={() => setDialogOpen(false)} color="secondary">Cancel</ArgosButton>
            <ArgosButton onClick={() => { handleUpdate(); setDialogOpen(false); }} color="primary">Save</ArgosButton>
          </DialogActions>}
        </Dialog>
    </ArgosFlexbox>

  );
};

export default renderUpdateID;
